// Lib
import { lowerFirst } from 'lodash/fp';

// Utils
import simpleTimeFormatter from '../../../../../common/utils/datetime/simpleTimeFormatter';
import {
    getDueDate,
    getHasDueDateTime,
    getIsTaskComplete,
    getShowDueDate,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import { getIsDueToday, getIsOverdue } from '../../../../../common/utils/timeUtil';
import { friendlyDateFormatterIntl } from '../../../../../common/utils/datetime/friendlyDateFormatterIntl';
import { FRIENDLY_DATE_FORMAT_STYLES } from '../../../../../common/utils/datetime/intlDateFormatPresets';
import { getFriendlyTranslatedTime } from '../../../../utils/i18n/translationHelpers';
import { DEFAULT_TIME_FORMAT } from '../../../../../common/users/timeFormatConstants';

export const getDueDatePopupId = (elementId) => `task-due-date-${elementId}`;

/**
 * Flat due-date data, decoupled from any element model so non-element callers
 * (e.g. Tiptap task items) can reuse the same formatting.
 * @typedef {object} DueDateData
 * @property {number|null} dueTimestamp - The due date timestamp, or null when there is no visible due date.
 * @property {boolean} hasDueDateTime - Whether the due date includes a specific time.
 * @property {boolean} isComplete - Whether the owning task is complete.
 */

/**
 * Extracts flat {@link DueDateData} from an element model.
 * @param {object} element - The task element
 * @returns {DueDateData}
 */
export const getDueDateData = (element) => ({
    dueTimestamp: getShowDueDate(element) ? (getDueDate(element) ?? null) : null,
    hasDueDateTime: getHasDueDateTime(element),
    isComplete: getIsTaskComplete(element),
});

export const getDueDateDataIsOverdue = ({ dueTimestamp, hasDueDateTime, isComplete }) => {
    if (isComplete || !dueTimestamp) return false;

    const dueDate = new Date(dueTimestamp);
    return getIsOverdue(dueDate, hasDueDateTime);
};

export const getDueDateDataIsDueToday = ({ dueTimestamp, isComplete }) => {
    if (isComplete || !dueTimestamp) return false;

    const dueDate = new Date(dueTimestamp);
    return getIsDueToday(dueDate);
};

export const getElementIsOverdue = (element) => getDueDateDataIsOverdue(getDueDateData(element));

export const getElementIsDueToday = (element) => getDueDateDataIsDueToday(getDueDateData(element));

/**
 * Get the due date pill content with translation support from flat data.
 * @param {DueDateData} dueDateData - Flat due-date data
 * @param {function} translate - The translation function from useTranslation hook (react-i18next t function)
 * @param {string} userLocale - The user's locale for date formatting
 * @param {string} timeFormat - The user's time format preference ('12h' or '24h')
 * @returns {string} The formatted due date text
 */
export const getDueDatePillContentFromData = (dueDateData, translate, userLocale, timeFormat = DEFAULT_TIME_FORMAT) => {
    const { dueTimestamp, hasDueDateTime } = dueDateData;

    if (!dueTimestamp) {
        return translate('assignment.dueDatePlaceholder');
    }

    const dueDate = new Date(dueTimestamp);

    // Show the relative time if it's overdue and in the past
    if (getDueDateDataIsDueToday(dueDateData) && getDueDateDataIsOverdue(dueDateData)) {
        const duePrefix = translate('assignment.dueDatePrefix');
        const friendlyTimeText = getFriendlyTranslatedTime(dueDate.getTime(), true, userLocale);
        return `${duePrefix} ${lowerFirst(friendlyTimeText)}`;
    }

    const dateText = lowerFirst(
        friendlyDateFormatterIntl({
            timestamp: dueDate,
            locale: userLocale,
            style: FRIENDLY_DATE_FORMAT_STYLES.COMPACT,
        }),
    );

    const duePrefix = translate('assignment.dueDatePrefix');
    const dueText = `${duePrefix} ${dateText}`;

    return hasDueDateTime ? `${dueText} (${simpleTimeFormatter(dueDate, timeFormat)})` : dueText;
};

export const getDueDateClassFromData = (dueDateData) => {
    if (!dueDateData.dueTimestamp) return '';

    if (dueDateData.isComplete) return 'completed';

    if (getDueDateDataIsOverdue(dueDateData)) return 'overdue';

    if (getDueDateDataIsDueToday(dueDateData)) return 'due-today';

    return 'due-future';
};

/**
 * Get the due date pill content with translation support
 * @param {object} element - The task element
 * @param {function} translate - The translation function from useTranslation hook (react-i18next t function)
 * @param {string} userLocale - The user's locale for date formatting
 * @param {string} timeFormat - The user's time format preference ('12h' or '24h')
 * @returns {string} The formatted due date text
 */
export const getDueDatePillContent = (element, translate, userLocale, timeFormat = DEFAULT_TIME_FORMAT) =>
    getDueDatePillContentFromData(getDueDateData(element), translate, userLocale, timeFormat);

export const getDueDateClass = (element) => getDueDateClassFromData(getDueDateData(element));
